import { getLogger } from "@expert/logging";
import { Box, Group, Tooltip } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { EvlIcon } from "@/common-ui/assets/image/EvlIcon";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { sdkEventBus, useAgentSdk } from "@/sdk";
import classes from "./EvlConnectionStatus.module.css";
import { type ConnectionStatus, DISCONNECT_DEBOUNCE_TIME_MS, STATUS_STYLES } from "./EvlStatusMapping";

const logger = getLogger({ module: "EvlConnectionStatus" });

export default function EvlConnectionStatus() {
    const sdk = useAgentSdk();
    const { technicalIssues } = usePartnerConfig();
    const [status, setStatus] = useState<ConnectionStatus>("connected");
    const disconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => {
        if (!technicalIssues?.autoSet) return;

        const unsubDisconnect = sdkEventBus.on("evl_disconnected", () => {
            if (disconnectTimeoutRef.current) return;

            setStatus("connecting");

            disconnectTimeoutRef.current = setTimeout(() => {
                disconnectTimeoutRef.current = undefined;
                setStatus("disconnected");
                if (sdk.getAgentActivity() === sdk.availableAgentActivity) {
                    void sdk.setAgentActivity(technicalIssues.agentActivity);
                }
            }, DISCONNECT_DEBOUNCE_TIME_MS);
        });

        const unsubReconnect = sdkEventBus.on("evl_reconnected", () => {
            if (disconnectTimeoutRef.current) {
                clearTimeout(disconnectTimeoutRef.current);
                disconnectTimeoutRef.current = undefined;
            }
            logger.info("EVL was re-connected.");
            setStatus("connected");
        });

        const timeout = disconnectTimeoutRef.current;

        return () => {
            unsubDisconnect();
            unsubReconnect();

            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [sdk, technicalIssues]);

    const { backgroundColor, iconColor, tooltip } = STATUS_STYLES[status];

    return (
        <Tooltip label={tooltip} zIndex={1000}>
            <Group>
                <Box
                    p={6}
                    className={classes.statusContainer}
                    style={{
                        backgroundColor: backgroundColor,
                    }}
                >
                    <EvlIcon color={iconColor} data-testid="evl-icon" />
                </Box>
            </Group>
        </Tooltip>
    );
}
